import classNames from "classnames";
import { get } from "lodash";
import { FieldsGrid, FieldObjectGrid } from "../../components/molecules";
import { STATUS, INDEX, EMPTY_STRING, EMPTY_ARRAY } from "../../constants";
import {
  FIELDS,
  AGGREGATE_FUNCTIONS,
  SORT_DIRECTION_OPTIONS,
  QUERY_PARAMETER_TYPES,
  QUERY_PARAMETER_COUNT,
  START_NODE_NAME,
} from "./datamodels.constants";
import {
  AssignInputField,
  ManageExpression,
  GroupBy,
  RequestBody,
  ServiceFileUpload,
  SmtpConnection,
  CustomFieldsGrid,
  OutputLinkPoints,
  CustomModelDefinition,
  SelectFields,
  ComputationField,
  FieldValueUpdates,
} from "./ServiceFields";
import {
  CustomRadioButton,
  CustomSelect,
  CustomToggle,
  CustomCheckBox,
  MultiEntry,
  CustomMultiSelectDropdown,
} from "../../components/atoms";
import ManageEditor from "./ServiceFields/ManageEditor";
import SelectFieldModal from "../../components/organisms/AppModals/ServiceModals/SelectFieldModal";
import { MapperModal }  from "./ServiceFields/MapperModal";

const { INACTIVE } = STATUS;
const { FIRST, SECOND } = INDEX;
const RESPONSE_MODEL = "Response Model";
const RULES_COLLECTION = "Rules Collection";
const MAPPER_MODAL = "Mapper Model";

const {
  EXPRESSION,
  SELECT,
  APPLY_RULES_SWITCH,
  LIST,
  FILES,
  CHECKBOX,
  CUSTOM_FIELD_ARRAY,
  RADIO_GROUP,
  CODE_EDITOR,
  REQUEST_BODY,
  TRANSACTIONS,
  SELECT_FIELD,
  COMPUTATION,
  SORTING,
  TEXT,
  OUTPUT_MODEL_TYPE,
  PREDEFINED,
  GROUP_BY,
  ASSIGN_INPUT,
  MODEL_NAME,
  RETURN_VALUE_MODEL,
  SELECT_CONNECTION,
  QUERY_PARAMS,
  HTTP_HEADERS,
  OUTPUT_LINK_POINTS,
  DEFINE_MODEL,
  MAPPER_MODEL,
  FIELD_UPDATES,
  TYPE,
  CUSTOM_MODEL,
  KEY_VALUE_PAIRS,
  URL_ENCODED,
  MULTI_SELECT_DROPDOWN,
  MULTI_SELECT,
  MULTI_ENTRY,
  OUTPUT_FIELDS,
  SUBFLOW,
} = FIELDS;

const LABEL = "label";
const CONTINENTS = "continents";

export const renderServiceField = (props) => {
  const {
    each,
    values,
    nodes,
    edges,
    collections,
    ruleCollections,
    subflowServices,
    setFieldValue,
    handleChange,
    outputLinks,
    inputLinks,
    removeHandle,
    currentNode,
  } = props;
  const { name, key, type, helperText, label } = each;
  const options = getOptions(each, collections, ruleCollections, subflowServices);

  switch (type) {
    case EXPRESSION:
      return (
        <ManageExpression
          name={name}
          values={values}
          nodes={nodes}
          edges={edges}
          expressionKey={key}
          handleChange={(e) => handleChange(e, key)}
          helperText={helperText}
          inputLinks={inputLinks}
        />
      );
    case CHECKBOX:
      return (
        <CustomCheckBox
          name={key}
          checked={values[key]}
          label={get(each, [CHECKBOX, LABEL])}
          handleChange={(e) => handleChange(e.target.checked, key)}
        />
      );
    case SELECT:
      return (
        <CustomSelect
          name={key}
          value={values[key]}
          options={options}
          handleChange={(e) => handleChange(e, key)}
          disabled={
            name === RETURN_VALUE_MODEL &&
            values[OUTPUT_MODEL_TYPE] !== PREDEFINED
          }
        />
      );
    case MULTI_ENTRY:
      return (
        <MultiEntry
          name={key}
          values={values[key]}
          options={options}
          handleChange={handleChange}
        />
      );
    case MULTI_SELECT_DROPDOWN:
      return (
        <CustomMultiSelectDropdown
          name={key}
          values={values[key]}
          options={options}
          handleChange={(e) => handleChange(e, key)}
          disabled={
            name === RETURN_VALUE_MODEL &&
            values[OUTPUT_MODEL_TYPE] !== PREDEFINED
          }
        />
      );
    case APPLY_RULES_SWITCH:
      return <CustomToggle name={key} checked={values[key]} />;
    case RADIO_GROUP:
      return <CustomRadioButton {...props} name={key} options={options} />;
    case SELECT_FIELD:
      return (
        <div
          className={classNames("bg-gray-900 rounded-md p-2", {
            "text-red-600": !values.modelName,
          })}
        >
          {values.modelName || "No valid model provided"}
        </div>
      );
    case REQUEST_BODY:
      return (
        <RequestBody
          values={values[key]}
          name={key}
          collections={collections}
          options={options}
          handleChange={handleChange}
        />
      );
    case FILES:
      return <ServiceFileUpload options={options} name={key} values={values} />;
    case CUSTOM_MODEL:
      return (
        <FieldsGrid
          name={key}
          values={values[key]}
          label="field"
          fields={[
            { name: "name", type: TEXT },
            { name: "type", options: QUERY_PARAMETER_TYPES, type: SELECT },
          ]}
          handleChange={handleChange}
        />
      );
    case LIST:
      return (
        <CustomFieldsGrid
          nameKey={key}
          values={values[key]}
          label={key}
          handleChange={handleChange}
          fields={[
            { name: "name", type: TEXT },
            { name: "type", options: QUERY_PARAMETER_TYPES, type: SELECT },
            { name: "count", options: QUERY_PARAMETER_COUNT, type: SELECT },
            { name: "description", type: TEXT },
            { name: "isRequired", type: CHECKBOX },
          ]}
          styleClasses={{ selectFields: "flex grid grid-cols-2 gap-2 my-1" }}
        />
      );
    case TRANSACTIONS:
      return (
        <FieldsGrid
          label="field"
          name={key}
          values={values[key]}
          fields={[{ name: "name", type: TEXT }]}
        />
      );
    case CUSTOM_FIELD_ARRAY:
      return (
        <FieldsGrid
          name={key}
          values={values[key]}
          label={"key-value pair"}
          fields={[
            { name: "key", type: TEXT },
            { name: "value", type: TEXT },
          ]}
        />
      );
    case KEY_VALUE_PAIRS:
      return (
        <FieldObjectGrid
          label={"key-value pair"}
          name={key}
          values={values}
          nodes={nodes}
          edges={edges}
          collections={collections}
          handleChange={handleChange}
          helperText={helperText}
          fields={[
            { name: "keyExp", type: TEXT },
            { name: "valueExp", type: TEXT },
          ]}
        />
      );
    case ASSIGN_INPUT:
      return (
        <AssignInputField
          name={key}
          node={currentNode}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
        />
      );
    case SELECT_CONNECTION:
      return (
        <SmtpConnection
          values={values[key]}
          handleSubmit={(e) => handleChange(e, key)}
        />
      );
    case COMPUTATION:
      return (
        <ComputationField
          name={key}
          values={values}
          label="computation"
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          fields={[
            { name: TYPE, options: AGGREGATE_FUNCTIONS },
            { name: SORTING, options: SORT_DIRECTION_OPTIONS },
          ]}
          collections={collections}
          nodes={nodes}
          edges={edges}
        />
      );
    case CODE_EDITOR:
      return (
        <ManageEditor
          label={name}
          name={key}
          value={values[key]}
          setFieldValue={setFieldValue}
        />
      );
    case OUTPUT_LINK_POINTS:
      return (
        <OutputLinkPoints
          outputLinks={outputLinks}
          handleChange={handleChange}
          removeHandle={removeHandle}
          currentNode={currentNode}
        />
      );
    case QUERY_PARAMS:
    case HTTP_HEADERS:
      return (
        <CustomFieldsGrid
          nameKey={key}
          values={values[key]}
          label={get(each, LABEL)}
          handleChange={handleChange}
          fields={[
            { name: "key", type: "expression" },
            { name: "value", type: "expression" },
            { name: "isRequired", type: CHECKBOX },
          ]}
          styleClasses={{ expressionFields: "flex flex-col gap-1" }}
          allValues={values}
          nodes={nodes}
          edges={edges}
        />
      );
    case DEFINE_MODEL:
      return (
        <CustomModelDefinition
          name={key}
          title={RESPONSE_MODEL}
          value={values[key]}
          handleChange={handleChange}
        />
      );
      case MAPPER_MODEL:
        return (
          <MapperModal
            name={key}
            title={MAPPER_MODAL}
            value={values[key]}
            handleChange={handleChange}
            allNodes={nodes}
            allEdges={edges}
            currentNode={currentNode}
            collections={collections}
          />
        );
    case GROUP_BY:
      return (
        <GroupBy
          name={key}
          values={values}
          collections={collections}
          handleChange={handleChange}
          nodes={nodes}
          edges={edges}
          setFieldValue={setFieldValue}
        />
      );
    case FIELD_UPDATES:
      return (
        <SelectFields
          values={values}
          name={key}
          setFieldValue={setFieldValue}
          collections={collections}
          handleChange={handleChange}
          nodes={nodes}
          edges={edges}
        />
      );
    case URL_ENCODED:
      return (
        <FieldsGrid
          name={key}
          values={values[key]}
          allValues={values}
          label={get(each, LABEL)}
          fields={[
            { name: "key", type: EXPRESSION },
            { name: "value", type: EXPRESSION },
          ]}
          styleClasses={{ expressionFields: "flex flex-col gap-1" }}
          handleChange={handleChange}
          nodes={nodes}
          edges={edges}
        />
      );
    case "field_value_updates":
      return (
        <FieldValueUpdates
          name={key}
          values={values}
          collections={collections}
          handleChange={handleChange}
          nodes={nodes}
          edges={edges}
        />
      );
    case MULTI_SELECT:
      return (
        <FieldsGrid
          name={key}
          values={values[key]}
          label={label}
          handleChange={handleChange}
          fields={[{ name: "value", type: SELECT, options: collections }]}
        />
      );
    case OUTPUT_FIELDS:
      return (
        <SelectFieldModal
          name={key}
          fieldKey={key}
          values={values}
          collections={ruleCollections}
          btnText="Manage Updates"
          title="Select fields to update"
          description="Select fields to update"
          modelName={values.modelName}
          handleChange={handleChange}
        />
      );
    default:
      return null;
  }
};

const getOptions = (each, collections, ruleCollections, subflowServices) => {
  const { type, name, key, options, radios } = each;
  if (name === RULES_COLLECTION) return ruleCollections;
  if (key === MODEL_NAME) return collections;
  else if (key === SUBFLOW) return subflowServices;
  else if (type === RADIO_GROUP) return radios;
  else return options;
};

const getCountryFromCities = (region, dataModelRegions) => {
  const city = region.split("/")[SECOND];
  if (
    dataModelRegions &&
    Object.keys(dataModelRegions.cities[FIRST]).includes(city)
  )
    return dataModelRegions.cities[FIRST][city];
};

export const getRegion = (dataModelRegions) => {
  const region = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const country = getCountryFromCities(region, dataModelRegions);
  let currentRegion;
  get(dataModelRegions, CONTINENTS, EMPTY_ARRAY).forEach((each) => {
    Object.entries(each).forEach(([key, value]) => {
      if (value.includes(country)) currentRegion = key;
    });
  });
  return currentRegion;
};

export const getDataModelSuggestionMessage = (
  selectedDataModel,
  dataModelRegions
) => {
  if (
    selectedDataModel.status === INACTIVE &&
    selectedDataModel.region !== getRegion(dataModelRegions)
  )
    return "Currently Database is in Inactive Mode and its region doesn't match with the current region. Please Update the region and mode by clicking on";
  else if (selectedDataModel.status === INACTIVE)
    return "Currently Database is in Inactive Mode. Please update mode by clicking on";
  else if (selectedDataModel.region !== getRegion(dataModelRegions))
    return "Current Region doesn't match with the selected data model's region. Please update the region by clicking on";
  else return EMPTY_STRING;
};

export const getStartNode = (nodes) =>
  nodes?.find(({ data }) => get(data, "name") === START_NODE_NAME);

const checkEdgeConnection = (targetId, nodes, edges, startNodeId) => {
  const currentNode = nodes.find(({ id }) => id === targetId);
  const inputHandleName = currentNode?.data.inputs.find(
    ({ custom }) => !custom
  )?.key;
  const inputHandleEdge =
    inputHandleName &&
    edges.find(
      ({ target, targetHandle }) =>
        target === targetId && targetHandle.includes(inputHandleName)
    );
  if (!inputHandleEdge) return false;

  if (inputHandleEdge.source === startNodeId) return true;
  return checkEdgeConnection(inputHandleEdge.source, nodes, edges, startNodeId);
};

export const checkIsConnectionComplete = (node, nodes, edges) => {
  const nodeId = node.id;
  const startNode = getStartNode(nodes);
  if (!startNode) return false;
  const startNodeId = startNode.id;
  if (startNodeId === nodeId) return true;
  return checkEdgeConnection(nodeId, nodes, edges, startNodeId);
};
